import { useState } from 'react';
import { Languages, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api, ApiError } from '@/lib/api';
import { TRANSLATION_TARGETS, TRANSLATION_DISCLAIMER } from '@/lib/languages';

interface Props {
  text: string;
  /** Best-effort default — usually the detected document language. */
  sourceLanguage?: string | null;
  /** When set, the backend records a `contract.translated` audit row
   *  scoped to this job so the activity feed can show the action. */
  jobId?: string | null;
  /** Fired after a successful translate so parents can refresh their
   *  activity feeds (e.g. JobCommentsPanel). */
  onTranslated?: () => void;
}

interface TranslateResponse {
  translated: string;
  target_language: string;
  source_language: string;
  input_tokens: number | null;
  output_tokens: number | null;
}

const TARGETS = TRANSLATION_TARGETS;

/**
 * Session-scoped translation toggle. Posts to /api/v1/contracts/translate
 * (Kaycoo MCP translate.text passthrough). Original text is NEVER persisted
 * — agents always read the source value.
 */
export function TranslateToggle({ text, sourceLanguage, jobId, onTranslated }: Props) {
  const [target, setTarget] = useState<string>('en');
  const [translated, setTranslated] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ src: string; in?: number; out?: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleTranslate() {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.post<TranslateResponse>('/api/v1/contracts/translate', {
        text,
        target_language: target,
        ...(sourceLanguage ? { source_language: sourceLanguage } : {}),
        ...(jobId ? { job_id: jobId } : {}),
      });
      setTranslated(resp.translated);
      setMeta({
        src: resp.source_language,
        in: resp.input_tokens ?? undefined,
        out: resp.output_tokens ?? undefined,
      });
      onTranslated?.();
    } catch (err: unknown) {
      const msg =
        err instanceof ApiError
          ? err.message
          : 'Translation failed. Please retry.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setTranslated(null);
    setMeta(null);
    setError(null);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Languages className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <label className="text-xs text-muted-foreground" htmlFor="translate-target">
          Translate to:
        </label>
        <select
          id="translate-target"
          className="rounded border bg-background px-2 py-1 text-xs"
          value={target}
          onChange={(e) => { setTarget(e.target.value); }}
          disabled={loading}
        >
          {TARGETS.map((t) => (
            <option key={t.code} value={t.code}>{t.label}</option>
          ))}
        </select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { void handleTranslate(); }}
          disabled={loading || text.trim().length === 0}
          className="h-7 text-xs gap-1"
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Languages className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {translated ? 'Re-translate' : 'Translate'}
        </Button>
        {translated && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="h-7 text-xs gap-1"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Show original
          </Button>
        )}
        {meta && (
          <span className="ml-auto text-[10px] text-muted-foreground">
            {meta.src} → {target}
            {meta.in != null && meta.out != null && ` · ${meta.in}+${meta.out} tokens`}
          </span>
        )}
      </div>

      {error && (
        <Alert variant="destructive" className="py-2">
          <AlertDescription className="text-xs">{error}</AlertDescription>
        </Alert>
      )}

      <pre className="text-xs whitespace-pre-wrap rounded bg-muted p-3 max-h-64 overflow-auto">
        {translated ?? text}
      </pre>
      {translated && (
        <p className="text-[10px] text-muted-foreground italic">
          {TRANSLATION_DISCLAIMER}
        </p>
      )}
    </div>
  );
}
